import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useCounterpartiesQueryData } from '@src/providers/Counterparties/useCounterpartiesQueryData';
import {
  Box,
  Dialog,
  DialogContent,
  DialogFooter,
  EMPTY_ARRAY,
  formattedDate,
  Input,
  Label,
  MixpanelEvent,
  MixpanelEventSource,
  type useDisclosureWithContext,
  useMarketAccountsContext,
  useMixpanel,
  VStack,
} from '@talos/kyoko';
import { uniqBy } from 'lodash-es';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useCreateManualBatchMutation, usePreviewSettlementBatchQuery } from '../useLpSettlementApi';
import { buildManualBatchCreatedEventProperties } from '../utils/lpSettlementMixpanelUtils';
import { ManualBatchPreviewSummary } from './ManualBatchPreviewSummary';
import type { NewManualBatchDisclosureContext } from './UnsettledTransactionsBlotter';

interface NewManualBatchDialogProps extends ReturnType<
  typeof useDisclosureWithContext<NewManualBatchDisclosureContext>
> {
  onSubmit: (batchID: string) => void;
}

export const NewManualBatchDialog = ({ onSubmit, ...disclosure }: NewManualBatchDialogProps) => {
  return (
    <Dialog
      {...disclosure}
      closeOnEscape
      title="New Manual Batch"
      showClose
      width={520}
      controlledContent
      suppressAutoEnterForm
      data-testid="new-manual-batch-dialog"
    >
      {disclosure.context && (
        <NewManualBatchDialogContent disclosure={disclosure} context={disclosure.context} onSubmit={onSubmit} />
      )}
    </Dialog>
  );
};

interface NewManualBatchDialogContentProps {
  context: NewManualBatchDisclosureContext;
  disclosure: ReturnType<typeof useDisclosureWithContext<NewManualBatchDisclosureContext>>;
  onSubmit: (batchID: string) => void;
}

function NewManualBatchDialogContent({ context, disclosure, onSubmit }: NewManualBatchDialogContentProps) {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { counterpartiesByName } = useCounterpartiesQueryData();
  const { homeCurrency } = useDisplaySettings();
  const mixpanel = useMixpanel(MixpanelEventSource.PortfolioLpSettlement);

  const selectedTrades = uniqBy(context?.selectedTrades ?? EMPTY_ARRAY, item => item.EventID);

  const summaryData = useMemo(() => {
    if (selectedTrades.length === 0) {
      return {
        counterparty: '',
        marketAccount: '',
        totalTransactions: '',
      };
    }

    return {
      counterparty: selectedTrades[0].Counterparty ?? '',
      marketAccount: selectedTrades[0].MarketAccount,
      totalTransactions: selectedTrades.length.toString(),
    };
  }, [selectedTrades]);

  const {
    isLoading: isPreviewLoading,
    data: batchData,
    isError: isPreviewError,
    refetch: retryPreviewSettlement,
    isSuccess: isPreviewSuccess,
  } = usePreviewSettlementBatchQuery({ selectedTrades, homeCurrency }, { skip: selectedTrades.length === 0 });
  const [createManualBatch, { isLoading: busy }] = useCreateManualBatchMutation();

  const settleBatchInstructions = useMemo(() => batchData?.[0]?.SettleBatchInstructions ?? EMPTY_ARRAY, [batchData]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isPreviewError) {
        retryPreviewSettlement();
      } else {
        if (!selectedTrades || selectedTrades.length === 0) {
          return;
        }
        createManualBatch({ selectedTrades, homeCurrency })
          .unwrap()
          .then(response => {
            const batchID = response?.[0]?.SettleBatch?.BatchID;
            if (!batchID) {
              return;
            }
            mixpanel.track(
              MixpanelEvent.LPSettlementManualBatchCreated,
              buildManualBatchCreatedEventProperties({
                reportCount: selectedTrades.length,
                instructionCount: settleBatchInstructions.length,
              })
            );
            onSubmit(batchID);
          });
      }
    },
    [
      selectedTrades,
      createManualBatch,
      homeCurrency,
      onSubmit,
      isPreviewError,
      retryPreviewSettlement,
      mixpanel,
      settleBatchInstructions,
    ]
  );

  const settlementDue = useMemo(() => {
    const deadlines = selectedTrades.map(trade => trade.SettleDeadline) ?? EMPTY_ARRAY;
    return deadlines.length > 0
      ? deadlines.reduce((min, current) => (current < min ? current : min), deadlines[0])
      : '';
  }, [selectedTrades]);

  return (
    <>
      <DialogContent overflow="hidden" style={{ order: 2, height: '100%', overflow: 'auto' }}>
        <form onSubmit={handleSubmit} id="new-manual-batch-modal-form">
          <ManualBatchPreviewSummary
            summaryData={summaryData}
            counterpartyDisplayName={
              counterpartiesByName?.get(summaryData.counterparty)?.DisplayName ?? summaryData.counterparty
            }
            marketAccountDisplayName={
              marketAccountsByName.get(summaryData.marketAccount)?.DisplayName ?? summaryData.marketAccount
            }
            homeCurrency={homeCurrency}
            settleBatchInstructions={settleBatchInstructions}
            isPreviewLoading={isPreviewLoading}
            isPreviewError={isPreviewError}
          />

          <VStack mt="spacing12" gap="spacing8" alignItems="flex-start">
            <Label>Settlement Due</Label>
            <Input
              value={formattedDate(settlementDue)}
              readOnly
              placeholder="Settlement due date"
              data-testid="settlement-due-input"
            />
          </VStack>
        </form>
      </DialogContent>
      <Box style={{ order: 3 }}>
        <DialogFooter
          showCancel
          showConfirm
          cancelLabel="Cancel"
          confirmLabel={isPreviewError ? 'Retry' : 'Create Manual Batch'}
          confirmDisabled={isPreviewLoading || busy || (isPreviewSuccess && settleBatchInstructions.length === 0)}
          confirmButtonType="submit"
          confirmButtonForm="new-manual-batch-modal-form"
          handleCancel={disclosure.close}
        />
      </Box>
    </>
  );
}
